import type { SectionTask } from './types'

// Number of completed tasks a section shows inline before collapsing the rest under the
// "N completed tasks..." expander.
export const COMPLETED_VISIBLE = 5

export interface SectionTaskGroups<T = SectionTask> {
    /** Not completed — always shown. */
    open: T[]
    /** Completed and on screen: the first COMPLETED_VISIBLE, or all when the section is expanded. */
    visibleCompleted: T[]
    /** Completed but collapsed under the "N completed tasks..." expander (empty when expanded). */
    hiddenCompleted: T[]
}

interface SplitOptions {
    showCompletedTasks?: boolean
    completedSortBy?: 'order' | 'recent'
    showAllCompleted?: boolean
}

// Single source of truth for how a section's tasks split into open / visible-completed /
// hidden-completed. Used both by Section (to render) and by the project-page description
// prefetch (to warm only on-screen tasks), so the "what's actually visible" rule lives in one
// place instead of being duplicated and drifting.
export function splitSectionTasks<T extends SectionTask>(
    tasks: T[],
    { showCompletedTasks = true, completedSortBy = 'order', showAllCompleted = false }: SplitOptions = {}
): SectionTaskGroups<T> {
    const open = tasks.filter(t => !t.isCompleted)

    if (!showCompletedTasks) {
        return { open, visibleCompleted: [], hiddenCompleted: [] }
    }

    const completed = tasks.filter(t => t.isCompleted).sort((a, b) => {
        // 'order' relies on completion pushing order down (recent-first within one list);
        // 'recent' sorts by updatedAt for views that merge tasks across lists.
        if (completedSortBy === 'recent') {
            return (b.updatedAt?.seconds ?? 0) - (a.updatedAt?.seconds ?? 0)
        }
        return a.order - b.order
    })

    if (showAllCompleted) {
        return { open, visibleCompleted: completed, hiddenCompleted: [] }
    }
    return {
        open,
        visibleCompleted: completed.slice(0, COMPLETED_VISIBLE),
        hiddenCompleted: completed.slice(COMPLETED_VISIBLE),
    }
}
